import { useContext, useEffect, useRef, useState } from "react";
import {
  classnames,
  getMatchedCSSRules,
  onTabKey,
  splitLast,
} from "lib/helpers";
import { useStyle } from "lib/hooks";
import { rootNamespace } from "config";
import { TreeContext } from "pages/Editor";

function SearchSelect({ placeholder, items = [], onSelect }) {
  const ref = useRef();
  const [show, setShow] = useState(false);
  const [value, setValue] = useState("");
  const [results, setResults] = useState([]);
  const [index, setIndex] = useState(0);
  const { subscribe } = useContext(TreeContext);
  useEffect(() => {
    const listener = ({ target }) => {
      if (!ref.current.contains(target)) {
        setShow(false);
      }
    };
    window.addEventListener("click", listener);
    return () => {
      window.removeEventListener("click", listener);
    };
  }, [ref, setShow]);
  const onChange = ({ target: { value } }) => {
    setValue(value);
    const regex = new RegExp(".*" + value + ".*", "gi");
    const filtered = items.filter((i) => regex.test(i));
    setResults(filtered);
    setShow(value.length !== 0);
  };
  const selectItem = (item) => {
    onSelect(item);
    setShow(false);
    setValue("");
  };
  function Result({ text, selected, onClick }) {
    return (
      <div onClick={onClick} className={classnames("sr-r", { s: selected })}>
        {text}
      </div>
    );
  }
  function onKeyDown({ keyCode, target: { value } }) {
    if (keyCode === 13) {
      if (results.length) selectItem(results[index]);
      selectItem(value);
    }
    if (keyCode === 38) {
      let nextIdx = index - 1;
      setIndex(index === 0 ? results.length - 1 : nextIdx);
    }
    if (keyCode === 40) {
      let nextIdx = index + 1;
      setIndex(index === results.length - 1 ? 0 : nextIdx);
    }
  }
  function onClick() {
    setShow(!show);
  }
  useEffect(() => {
    return subscribe(rootNamespace, "ESC", () => setShow(false));
  }, [subscribe]);
  return (
    <div className="vc f" ref={ref}>
      <input {...{ placeholder, onChange, value, onKeyDown, onClick }} />
      {results.length > 0 && show && (
        <div className="sr">
          {results.map((r, i) => {
            return (
              <Result
                key={i}
                text={r}
                onClick={() => selectItem(results[i])}
                selected={i === index}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function CSSAttribute({ attr, value, css, path }) {
  const { currentCSS, setCurrentCSS, publish } = useContext(TreeContext);
  const [style, setStyle] = useStyle(attr);
  const key = useRef();
  const val = useRef();
  const [editing, setEditing] = useState(-1);
  const onDoubleClick = (idx) => () => {
    setCurrentCSS({ path, idx });
  };
  const onKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      if (css) {
        css.setProperty(attr, val.current.innerText);
      } else {
        setStyle(val.current.innerText);
      }
      setEditing(-1);
    }
  };
  useEffect(() => {
    if (editing > -1) {
      if (editing === 0) key.current.focus();
      else val.current.focus();
      document.execCommand("selectAll");
    }
  }, [editing]);
  // from stylesheet
  useEffect(() => {
    val.current.innerText = css ? css[attr] : value;
  }, [css, attr, value]);
  // from data
  useEffect(() => {
    val.current.innerText = style;
  }, [style]);
  useEffect(() => {
    key.current.innerText = attr;
  }, [attr]);
  useEffect(() => {
    const listener = (e) => {
      onTabKey(() => {
        const { shiftKey } = e;
        if (editing > -1) {
          if (editing === 1 && shiftKey) setEditing(0);
          else if (editing === 0 && !shiftKey) setEditing(1);
          else {
            if (editing === 0 && shiftKey) {
              publish(rootNamespace, "PREV_CSS");
            } else if (editing === 1) {
              publish(rootNamespace, "NEXT_CSS");
            }
            setEditing(-1);
          }
        }
      })(e);
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [editing, publish]);
  useEffect(() => {
    if (currentCSS.path === path) {
      setEditing(currentCSS.idx);
    }
  }, [currentCSS, path]);
  return (
    <div className="sd-i">
      <div className="sd-k">
        <span
          ref={key}
          onDoubleClick={onDoubleClick(0)}
          contentEditable={editing === 0}
        ></span>
        <span className="d">:</span>
      </div>
      <div className="sd-v">
        <span
          ref={val}
          {...{ onKeyDown }}
          onDoubleClick={onDoubleClick(1)}
          contentEditable={editing === 1}
        ></span>
        <span className="d">;</span>
      </div>
    </div>
  );
}

function CSSBlock({ title, styles, css = false }) {
  const isPojo = (obj) => Object.getPrototypeOf(obj) === Object.prototype;
  const keys = isPojo(styles) ? Object.keys(styles) : [...styles];
  return (
    <div className="sd-r">
      <div>
        {title} {"{"}
      </div>
      <div>
        {keys.map((key) => {
          const props = {};
          if (css) props.css = styles;
          else props.value = styles[key];
          return (
            <CSSAttribute
              path={title + "." + key}
              attr={key}
              {...{ key, ...props }}
            />
          );
        })}
      </div>
      <div>{"}"}</div>
    </div>
  );
}

export default function CustomCSS() {
  // tab traverse css helper
  const [currentCSS, setCurrentCSS] = useState({});
  const { selection, update, subscribe } = useContext(TreeContext);
  // needed to re populate style whenever it changes
  const [style] = useStyle();
  function onSelect(item) {
    update(selection.path + ".style", { [item]: "" });
  }
  // all stylesheets + stored style
  const [styles, setStyles] = useState({});
  // make effect run both whenever a style is changed
  // and when selection is changed
  useEffect(() => {
    let styles = {};
    if (style) styles.inline = style;
    if (selection) styles = { ...styles, ...getMatchedCSSRules(selection.ref) };
    setStyles(styles);
  }, [selection, style]);
  useEffect(() => {
    return subscribe(rootNamespace, "PREV_CSS", () => {
      if (currentCSS.path.startsWith("inline")) {
        const keys = Object.keys(styles.inline);
        const idx = keys.indexOf(splitLast(currentCSS.path, "."));
        if (idx > 0) {
          setCurrentCSS({ path: "inline." + keys[idx - 1], idx: 1 });
        }
      } else {
      }
    });
  }, [currentCSS, styles, subscribe]);
  useEffect(() => {
    return subscribe(rootNamespace, "NEXT_CSS", () => {
      if (currentCSS.path.startsWith("inline")) {
        const keys = Object.keys(styles.inline);
        const idx = keys.indexOf(splitLast(currentCSS.path, "."));
        if (idx + 1 < keys.length) {
          setCurrentCSS({ path: "inline." + keys[idx + 1], idx: 0 });
        }
      } else {
      }
    });
  }, [currentCSS, styles, subscribe]);
  const entries = Object.entries(styles);
  return (
    <div>
      <div className="pr">
        <SearchSelect placeholder="Search properties..." onSelect={onSelect} />
      </div>
      {entries.length > 0 && (
        <>
          <div className="pr h">
            <div className="pr-h">Styles</div>
          </div>
          <div className="sd">
            <TreeContext.Provider
              value={{ selection, update, currentCSS, setCurrentCSS }}
            >
              {Object.entries(styles).map(([stylesheet, values]) => {
                const renderRules = (rules) => {
                  return (
                    <>
                      <div className="sd-t">{stylesheet}</div>
                      {Object.entries(rules).map(([title, styles], key) => {
                        return (
                          <CSSBlock {...{ key, title, styles }} css={true} />
                        );
                      })}
                    </>
                  );
                };
                const renderStyles = (styles) => {
                  return <CSSBlock title="inline" {...{ styles }} />;
                };
                return (
                  <div key={stylesheet} className="sd-c">
                    {stylesheet === "inline"
                      ? renderStyles(values)
                      : renderRules(values)}
                  </div>
                );
              })}
            </TreeContext.Provider>
          </div>
        </>
      )}
    </div>
  );
}
